// Runtime type guards for JSON fields from the database
import type { ServiceTabConfig } from '@/data/serviceTabPresets';

/**
 * Type guard to check if a value is a valid ServiceTabConfig
 */
export function isServiceTabConfig(value: unknown): value is ServiceTabConfig {
  if (typeof value !== 'object' || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    typeof obj.id === 'string' &&
    typeof obj.label === 'string' &&
    typeof obj.icon === 'string'
  );
}

/**
 * Type guard to check if a value is an array of ServiceTabConfig
 */
export function isServiceTabConfigArray(value: unknown): value is ServiceTabConfig[] {
  if (!Array.isArray(value)) return false;
  return value.every(isServiceTabConfig);
}

/**
 * Safely parse service_tabs from JSON, returning empty array if invalid
 */
export function parseServiceTabs(value: unknown): ServiceTabConfig[] {
  if (isServiceTabConfigArray(value)) {
    return value;
  }
  return [];
}

/**
 * Type guard for social links object
 */
export function isSocialLinksObject(value: unknown): value is Record<string, string> {
  if (typeof value !== 'object' || value === null) return false;
  return Object.values(value).every((v) => typeof v === 'string' || v === null);
}

/**
 * Safely parse social links from JSON
 */
export function parseSocialLinks(value: unknown): Record<string, string> {
  if (isSocialLinksObject(value)) {
    return value as Record<string, string>;
  }
  return {};
}

/**
 * Type guard for automation rule metadata
 */
export function isAutomationMetadata(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Safely parse automation metadata from JSON
 */
export function parseAutomationMetadata(value: unknown): Record<string, unknown> {
  if (isAutomationMetadata(value)) {
    return value;
  }
  return {};
}
